import type { ElementType, ReactNode } from "react";
import styled, { css } from "styled-components";

import { DefaultTheme } from "../themes";

/**
 * [共通パーツ]テキスト
 */

export type TypographyProps = {
  className?: string;
  as?: ElementType;
  size?: 1 | 2 | 3 | 4 | 5 | 6;
  bold?: boolean;
  textRight?: boolean;
  withEllipsis?: boolean;
  breakWord?: boolean;
  children: ReactNode;
};

const getFontStyle = (props: TypographyProps & { theme: DefaultTheme }) => {
  switch (props.size) {
    case 1:
      return {
        size: props.theme.fontSize.xsmall,
        letterSpacing: 0,
      };

    case 2:
      return {
        size: props.theme.fontSize.small,
        letterSpacing: 0,
      };

    case 3:
      return {
        size: props.theme.fontSize.medium,
        letterSpacing: 0,
      };

    case 4:
      return {
        size: props.theme.fontSize.large,
        letterSpacing: "0.05em",
      };

    case 5:
      return {
        size: props.theme.fontSize.xlarge,
        letterSpacing: 0,
      };

    case 6:
      return {
        size: props.theme.fontSize.xxlarge,
        letterSpacing: "0.04em",
      };

    default:
      return {
        size: props.theme.fontSize.medium,
        letterSpacing: 0,
      };
  }
};

export const TypographyStyle = css<TypographyProps>`
  font-weight: ${(props) => (props.bold ? "bold" : "normal")};
  font-size: ${(props) => getFontStyle(props).size};
  letter-spacing: ${(props) => getFontStyle(props).letterSpacing};
  line-height: 1.2;
  ${(props) => props.textRight && `text-align: right; width: 100%;`}
  ${(props) => props.breakWord && `word-break: break-all;`};
  ${(props) =>
    props.withEllipsis &&
    `overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`};
`;

const transientProps = ["bold", "withEllipsis", "breakWord", "textRight"];

const StyledText = styled.p.withConfig({
  shouldForwardProp: (prop) => !transientProps.includes(prop),
})<TypographyProps>`
  ${TypographyStyle}
`;

export function Typography({
  as,
  size = 3,
  bold = false,
  textRight,
  withEllipsis = false,
  breakWord = false,
  children,
  className,
}: TypographyProps) {
  return (
    <StyledText
      as={as}
      className={className}
      size={size}
      bold={bold}
      textRight={textRight}
      withEllipsis={withEllipsis}
      breakWord={breakWord}
    >
      {children}
    </StyledText>
  );
}
